import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, Package, Tag, DollarSign, Scan, Plus, Minus, TrendingUp, TrendingDown, CheckCircle2, XCircle, Star } from 'lucide-react'
import { api } from '../../../lib/api'
import { useStore } from '../../../contexts/StoreContext'
import { useStoreValidation } from '../../../hooks/useStoreValidation'
import toast from 'react-hot-toast'
import ProductImage from '../../../components/ProductImage'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'

interface ProductVariant {
  id: string
  variantName: string
  variantType: 'flavor' | 'color' | 'size' | 'other'
  stockQuantity: number
  sku?: string
  imageUrl?: string
  isAvailable: boolean
}

interface Product {
  id: string
  name: string
  description: string
  price: number
  image_url?: string
  category_id: string
  store_id: string
  is_available: boolean
  stock_quantity: number
  unit: string
  is_featured: boolean
  barcode?: string
  sku?: string
  weight_kg?: number
  brand?: string
  tags?: string[]
  created_at: string
  updated_at: string
  variants?: ProductVariant[]
  has_variants?: boolean
  category?: {
    id: string
    name: string
    slug: string
  }
}

const ViewProduct = () => {
  const { storeId, productId } = useParams<{ storeId: string; productId: string }>()
  const navigate = useNavigate()
  const { activeStore } = useStore()
  const { requireApprovedStore } = useStoreValidation()

  const [loading, setLoading] = useState(true)
  const [product, setProduct] = useState<Product | null>(null)
  const [stockQuantity, setStockQuantity] = useState(1)
  const [updatingStock, setUpdatingStock] = useState(false)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const headerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (productId && (storeId || activeStore?.id)) {
      loadProduct()
    }
  }, [productId, storeId, activeStore])

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640 // sm breakpoint

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      // Ocultar al hacer scroll hacia abajo, mostrar al hacer scroll hacia arriba
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  const loadProduct = async () => {
    if (!productId) return

    const cacheKey = `view_product_${productId}`
    const cachedProduct = getCacheStale<Product>(cacheKey)
    if (cachedProduct) {
      setProduct(cachedProduct)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/products/${productId}`)
      
      if (response.data.success && response.data.data) {
        const productData = response.data.data
        const normalizedProduct = {
          ...productData,
          price: typeof productData.price === 'string' ? parseFloat(productData.price) : Number(productData.price) || 0,
          stock_quantity: typeof productData.stock_quantity === 'string' ? parseInt(productData.stock_quantity) : Number(productData.stock_quantity) || 0
        }
        setProduct(normalizedProduct)
        setCache(cacheKey, normalizedProduct, 30 * 60 * 1000)
      } else {
        toast.error('Producto no encontrado')
        navigate(-1)
      }
    } catch (error: any) {
      console.error('Error loading product:', error)
      
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedProduct) {
        return
      }
      
      toast.error('Error al cargar el producto')
      navigate(-1)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStock = async (operation: 'add' | 'subtract' | 'set') => {
    if (!product || !storeId) return

    setUpdatingStock(true)
    try {
      let result
      const currentStoreId = storeId || activeStore?.id
      
      if (product.barcode && currentStoreId) {
        result = await api.updateProductStockByBarcode(
          currentStoreId,
          product.barcode,
          stockQuantity,
          operation
        )
      } else if (product.id) {
        result = await api.updateProductStock(product.id, stockQuantity, operation)
      }

      if (result?.success) {
        toast.success('Stock actualizado correctamente')
        await loadProduct()
        setStockQuantity(1)
      } else {
        toast.error('Error al actualizar el stock')
      }
    } catch (error: any) {
      console.error('Error updating stock:', error)
      toast.error('Error al actualizar el stock')
    } finally {
      setUpdatingStock(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="text-gray-600 font-semibold">Cargando producto...</p>
        </div>
      </div>
    )
  }

  if (!product) {
    return null
  }

  const currentStoreId = storeId || activeStore?.id

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      {/* Header - Ocultable en scroll en pantallas pequeñas */}
      <div
        ref={headerRef}
        className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 transition-transform duration-300 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>
              <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex-1 text-center truncate px-2">
                Detalles del Producto
              </h1>
              <button
                onClick={() => navigate(`/store/${currentStoreId}/products/${product.id}/edit`)}
                className="px-3 sm:px-5 py-2 sm:py-3 bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 active:scale-95 flex items-center gap-1.5 sm:gap-2 flex-shrink-0"
              >
                <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Editar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Imagen del Producto - Compacto */}
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8 overflow-hidden">
            <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl overflow-hidden flex items-center justify-center">
              {product.image_url ? (
                <ProductImage
                  imageKey={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Package className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 text-gray-400" />
              )}
            </div>
          </div>

          {/* Información del Producto - Compacto */}
          <div className="space-y-4 sm:space-y-6">
            {/* Nombre y Estado */}
            <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8">
              <div className="flex items-start justify-between mb-4 sm:mb-6 gap-2">
                <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex-1 pr-2 sm:pr-4">
                  {product.name}
                </h2>
                {product.is_featured && (
                  <div className="flex-shrink-0">
                    <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-yellow-400 to-amber-400 rounded-xl sm:rounded-2xl shadow-xl border border-yellow-300/50">
                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-900 fill-yellow-900" />
                      <span className="text-xs sm:text-sm font-bold text-yellow-900">Destacado</span>
                    </div>
                  </div>
                )}
              </div>

              {product.description && (
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed font-medium">
                  {product.description}
                </p>
              )}

              {/* Estado de Disponibilidad - Compacto */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                {product.is_available ? (
                  <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-50/80 text-emerald-700 border border-emerald-200/50 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-bold">Disponible</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-red-50/80 text-red-700 border border-red-200/50 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                    <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-bold">No Disponible</span>
                  </div>
                )}
                {product.category && (
                  <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-50/80 text-indigo-700 border border-indigo-200/50 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                    <Tag className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-bold">{product.category.name}</span>
                  </div>
                )}
              </div>

              {/* Precio - Compacto */}
              <div className="flex items-baseline gap-2 sm:gap-3">
                <DollarSign className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-blue-600" />
                <span className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  {typeof product.price === 'number' ? product.price.toFixed(2) : Number(product.price || 0).toFixed(2)}
                </span>
                <span className="text-sm sm:text-base lg:text-lg text-gray-500 font-semibold">RD$</span>
              </div>
            </div>

            {/* Información de Stock - Compacto */}
            <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-blue-600" />
                Gestión de Stock
              </h3>

              {/* Stock Actual */}
              <div className="mb-6 p-6 bg-white/40 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-semibold">Stock Actual</p>
                    <p className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      {product.stock_quantity} <span className="text-xl text-gray-600">{product.unit}</span>
                    </p>
                  </div>
                  {product.stock_quantity > 0 ? (
                    <TrendingUp className="w-12 h-12 text-emerald-600" />
                  ) : (
                    <TrendingDown className="w-12 h-12 text-red-600" />
                  )}
                </div>
              </div>

              {/* Selector de Cantidad */}
              <div className="mb-6">
                <label className="block text-base font-bold text-gray-900 mb-4">
                  Cantidad
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setStockQuantity(Math.max(1, stockQuantity - 1))}
                    disabled={updatingStock}
                    className="w-16 h-16 bg-white/60 backdrop-blur-sm hover:bg-white/80 border border-white/30 rounded-2xl flex items-center justify-center transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                  >
                    <Minus className="w-6 h-6 text-gray-700" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={stockQuantity}
                    onChange={(e) => setStockQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    disabled={updatingStock}
                    className="flex-1 px-6 py-4 border-2 border-white/30 rounded-2xl text-center text-2xl font-bold text-gray-900 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:opacity-50 bg-white/60 backdrop-blur-sm"
                  />
                  <button
                    onClick={() => setStockQuantity(stockQuantity + 1)}
                    disabled={updatingStock}
                    className="w-16 h-16 bg-white/60 backdrop-blur-sm hover:bg-white/80 border border-white/30 rounded-2xl flex items-center justify-center transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                  >
                    <Plus className="w-6 h-6 text-gray-700" />
                  </button>
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={() => handleUpdateStock('subtract')}
                  disabled={updatingStock || stockQuantity > product.stock_quantity}
                  className="px-4 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl text-sm font-bold hover:from-red-700 hover:to-red-800 transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl"
                >
                  Restar
                </button>
                <button
                  onClick={() => handleUpdateStock('add')}
                  disabled={updatingStock}
                  className="px-4 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl text-sm font-bold hover:from-emerald-700 hover:to-emerald-800 transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl"
                >
                  Agregar
                </button>
                <button
                  onClick={() => handleUpdateStock('set')}
                  disabled={updatingStock}
                  className="px-4 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl text-sm font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl"
                >
                  Establecer
                </button>
              </div>

              {updatingStock && (
                <div className="mt-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-white/60 backdrop-blur-sm rounded-2xl mb-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-4 border-blue-200 border-t-blue-600"></div>
                  </div>
                  <p className="text-sm text-gray-600 font-semibold">Actualizando stock...</p>
                </div>
              )}
            </div>

            {/* Información Adicional */}
            <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Información Adicional</h3>
              <div className="space-y-4">
                {product.barcode && (
                  <div className="flex items-center gap-4 p-4 bg-white/40 backdrop-blur-sm rounded-2xl border border-white/30">
                    <Scan className="w-6 h-6 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-1 font-semibold">Código de Barras</p>
                      <p className="text-base font-mono font-bold text-gray-900">{product.barcode}</p>
                    </div>
                  </div>
                )}
                {product.sku && (
                  <div className="flex items-center gap-4 p-4 bg-white/40 backdrop-blur-sm rounded-2xl border border-white/30">
                    <Package className="w-6 h-6 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-1 font-semibold">SKU</p>
                      <p className="text-base font-bold text-gray-900">{product.sku}</p>
                    </div>
                  </div>
                )}
                {product.brand && (
                  <div className="flex items-center gap-4 p-4 bg-white/40 backdrop-blur-sm rounded-2xl border border-white/30">
                    <Tag className="w-6 h-6 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-1 font-semibold">Marca</p>
                      <p className="text-base font-bold text-gray-900">{product.brand}</p>
                    </div>
                  </div>
                )}
                {product.weight_kg && (
                  <div className="flex items-center gap-4 p-4 bg-white/40 backdrop-blur-sm rounded-2xl border border-white/30">
                    <Package className="w-6 h-6 text-gray-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-600 mb-1 font-semibold">Peso</p>
                      <p className="text-base font-bold text-gray-900">{product.weight_kg} kg</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Variantes */}
            {product.variants && product.variants.length > 0 && (
              <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Variantes</h3>
                <div className="space-y-4">
                  {product.variants.map((variant) => (
                    <div key={variant.id} className="p-5 bg-white/40 backdrop-blur-sm rounded-2xl border border-white/30">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-base font-bold text-gray-900">{variant.variantName}</p>
                          <p className="text-sm text-gray-600 capitalize font-medium">{variant.variantType}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {variant.stockQuantity} {product.unit}
                          </p>
                          {variant.sku && (
                            <p className="text-xs text-gray-500 font-mono">{variant.sku}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewProduct
